import { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import Button from "@components/Button";
import InlineAlert from "@components/InlineAlert";

interface EmailLinkFormProps {
    hasSentLink: boolean;
    setHasSentLink: (hasSentLink: boolean) => void;
    email: string;
    setEmail: (email: string) => void;
}

export default function EmailLinkForm({
    hasSentLink,
    setHasSentLink,
    email,
    setEmail
}: EmailLinkFormProps) {
    const { sendLoginLink, error, emailLinkSent } = useAuth();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isResending, setIsResending] = useState(false);
    const [resendSuccess, setResendSuccess] = useState(false);
    const [resendCooldown, setResendCooldown] = useState(0);

    useEffect(() => {
        if (resendCooldown > 0) {
            const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
            return () => clearTimeout(timer);
        }
    }, [resendCooldown]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);
        try {
            await sendLoginLink(email);
            setHasSentLink(true);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleResendLink = async () => {
        setIsResending(true);
        setResendSuccess(false);
        try {
            await sendLoginLink(email);
            setResendSuccess(true);
            setHasSentLink(true);
            setResendCooldown(60);
        } finally {
            setIsResending(false);
        }
    };

    return (
        <>
            {/* Error Display */}
            {error && (
                <InlineAlert level="danger">
                    <p className="font-medium">Error</p>
                    <p>{error}</p>
                </InlineAlert>
            )}
            {hasSentLink ? (
                <>
                    <div className="flex flex-col gap-4 text-center">
                        <p>
                            We sent an email to <strong>{email}</strong>. <br />
                            Open the link in the email to continue.
                        </p>
                        <div className="grid grid-cols-2 gap-2">
                            <a
                                href={"https://mail.google.com/mail/u/0/#search/roboflow"}
                                target="_blank"
                                className="btn2 medium secondary !h-auto py-3"
                                rel="noreferrer"
                            >
                                <img src="/images/gmail_logo.svg" alt="Gmail" className="h-4 w-4" />
                                Open Gmail
                            </a>
                            <a
                                href={"https://outlook.live.com/mail/"}
                                target="_blank"
                                className="btn2 secondary medium !h-auto py-3"
                                rel="noreferrer"
                            >
                                <img
                                    src="/images/outlook_logo.svg"
                                    alt="Outlook"
                                    className="h-4 w-4"
                                />
                                Open Outlook
                            </a>
                        </div>
                    </div>
                    <p className="text-center text-xs text-tertiary">
                        Note: The link is only valid for a short time and can only be used once.
                        <br />
                        If you don't see the email, check your spam folder.
                    </p>
                    <hr className="my-0" />
                    <div className="flex flex-col items-center justify-center gap-2">
                        <p className="text-sm text-tertiary">Can't find it?</p>
                        <div className="flex gap-2">
                            <Button type="secondary" onClick={() => setHasSentLink(false)}>
                                <i className="far fa-arrow-left"></i>
                                Go Back
                            </Button>
                            <Button
                                type="secondary"
                                onClick={handleResendLink}
                                disabled={isResending || resendCooldown > 0}
                            >
                                {isResending
                                    ? "Resending..."
                                    : resendSuccess && resendCooldown > 0
                                      ? `Sent! Try again in ${resendCooldown}s`
                                      : "Resend Link"}
                            </Button>
                        </div>
                    </div>
                </>
            ) : (
                <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                    <div className="inputWithLabel">
                        <label htmlFor="email-link">Email</label>
                        <input
                            id="email-link"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            placeholder="Enter your email"
                            className="form-input"
                        />
                    </div>

                    <Button
                        type="primary"
                        disabled={isSubmitting || !email}
                        onClick={handleSubmit}
                        loading={isSubmitting}
                        className="!h-auto py-3"
                    >
                        Continue
                    </Button>
                </form>
            )}
        </>
    );
}
